import Text from '@cartwhl/ui/base/TextNew';
import CharacterIcon from '@cartwhl/ui/icons/CharacterIcon';
import Overlay from '@cartwhl/ui/player/Overlay';

import {
  ENABLE_CHARACTER_PICKER_PANEL,
  ENABLE_HISTORY_PANEL,
} from '../../feature-flags';
import { AdvancedIcon, HistoryIcon } from '../icons';
import type { MotionEditorPanel } from './types';

type OverlayPlacement =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

interface MotionEditorPanelButtonsProps {
  openPanel: MotionEditorPanel | null;
  onOpenPanelChange: (panel: MotionEditorPanel | null) => void;
  placement?: OverlayPlacement;
  selectedCharacterMeta?: {
    heroThumbnailPath?: string;
    characterName?: string;
  } | null;
}

export default function MotionEditorPanelButtons({
  openPanel,
  onOpenPanelChange,
  placement = 'bottom-right',
  selectedCharacterMeta,
}: MotionEditorPanelButtonsProps) {
  const togglePanel = (panel: MotionEditorPanel) => {
    onOpenPanelChange(openPanel === panel ? null : panel);
  };
  return (
    <>
      <Overlay.Panel
        placement={placement}
        widthClassName="w-[180px]"
        collapsible
        defaultCollapsed
      >
        <Overlay.PanelHeader
          icon={<CharacterPanelIcon />}
          title="Character & scene"
        />
        <Overlay.Content>
          {ENABLE_CHARACTER_PICKER_PANEL && (
            <Overlay.PreviewButton
              tooltipText="Character"
              text="Character"
              icon={<CharacterIcon className="size-4" />}
              imageSrc={selectedCharacterMeta?.heroThumbnailPath}
              crossOrigin="use-credentials"
              imageAlt={
                selectedCharacterMeta?.characterName
                  ? `Current character is ${selectedCharacterMeta.characterName}`
                  : 'Character'
              }
              fallback={<CharacterIcon className="size-4" />}
              active={openPanel === 'character'}
              onClick={() => togglePanel('character')}
            />
          )}
          <Overlay.Button
            tooltipText="Stage options"
            active={openPanel === 'stage'}
            text="Stage options"
            icon={<AdvancedIcon />}
            onClick={() => togglePanel('stage')}
          />
        </Overlay.Content>
      </Overlay.Panel>

      {ENABLE_HISTORY_PANEL && (
        <Overlay.Panel
          placement={placement}
          widthClassName="w-[180px] max-sm:w-[72px]"
        >
          <Overlay.PanelHeader
            icon={<HistoryIcon />}
            onClick={() => togglePanel('history')}
            aria-pressed={openPanel === 'history'}
            className="max-sm:justify-center"
          >
            <Text
              token="12/medium"
              className="text-primary min-w-0 flex-1 truncate max-sm:sr-only"
            >
              History
            </Text>
          </Overlay.PanelHeader>
        </Overlay.Panel>
      )}
    </>
  );
}

const CharacterPanelIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path d="M1 14.75C1 15.3023 1.44772 15.75 2 15.75H5V16.75H2C0.96435 16.75 0.113005 15.9628 0.0107422 14.9541L0 14.75V13.75H1V14.75ZM19 14.75C19 15.7857 18.2128 16.637 17.2041 16.7393L17 16.75H14V15.75H17C17.5523 15.75 18 15.3023 18 14.75V13.75H19V14.75ZM9.5 4.75C10.9582 4.75162 12.3566 5.33121 13.3877 6.3623C14.4188 7.3934 14.9984 8.79181 15 10.25C15 11.3378 14.6776 12.4012 14.0732 13.3057C13.4689 14.2101 12.6095 14.9148 11.6045 15.3311C10.5995 15.7473 9.49361 15.8567 8.42676 15.6445C7.35994 15.4323 6.38047 14.9078 5.61133 14.1387C4.84219 13.3695 4.31771 12.3901 4.10547 11.3232C3.89326 10.2564 4.00271 9.15047 4.41895 8.14551C4.83523 7.14051 5.53987 6.28111 6.44434 5.67676C7.34879 5.07243 8.41222 4.75 9.5 4.75ZM11.1367 6.29785C10.3551 5.97417 9.49475 5.88965 8.66504 6.05469C7.8354 6.2198 7.07374 6.62743 6.47559 7.22559C5.87744 7.82374 5.4698 8.5854 5.30469 9.41504C5.13965 10.2448 5.22417 11.1051 5.54785 11.8867C5.87158 12.6683 6.41973 13.3366 7.12305 13.8066C7.82652 14.2767 8.65394 14.5273 9.5 14.5273C10.6342 14.5261 11.7215 14.0754 12.5234 13.2734C13.3254 12.4715 13.7761 11.3842 13.7773 10.25C13.7773 9.40394 13.5267 8.57652 13.0566 7.87305C12.5866 7.16973 11.9183 6.62158 11.1367 6.29785ZM11.6201 10.8564C11.7005 10.8667 11.7785 10.8931 11.8486 10.9336C11.9188 10.9742 11.9803 11.0283 12.0293 11.0928C12.0784 11.1574 12.1145 11.231 12.1348 11.3096C12.1551 11.3882 12.1592 11.4704 12.1475 11.5508C12.1358 11.6309 12.1081 11.7079 12.0664 11.7773C11.5062 12.748 10.5694 13.3057 9.5 13.3057C8.43056 13.3057 7.49531 12.7485 6.93359 11.7773C6.85625 11.6373 6.83708 11.4725 6.87988 11.3184C6.92273 11.1641 7.02447 11.0332 7.16309 10.9531C7.30176 10.873 7.46604 10.8506 7.62109 10.8906C7.77616 10.9306 7.90958 11.0298 7.99219 11.167C8.19038 11.5098 8.64978 12.083 9.5 12.083C10.3502 12.083 10.8096 11.5092 11.0078 11.167C11.0471 11.096 11.1003 11.0327 11.1641 10.9824C11.2277 10.9323 11.3009 10.8957 11.3789 10.874C11.4571 10.8523 11.5396 10.8462 11.6201 10.8564ZM7.8252 8.43262C7.98325 8.46406 8.12921 8.54132 8.24316 8.65527C8.35698 8.76915 8.43436 8.91437 8.46582 9.07227C8.49723 9.23017 8.48144 9.3942 8.41992 9.54297C8.35825 9.69186 8.25314 9.81965 8.11914 9.90918C7.98529 9.99855 7.82794 10.0458 7.66699 10.0459C7.45096 10.0459 7.24361 9.96033 7.09082 9.80762C6.93801 9.65481 6.85156 9.44755 6.85156 9.23145C6.85157 9.0703 6.89973 8.91231 6.98926 8.77832C7.07873 8.64457 7.20585 8.54014 7.35449 8.47852C7.50327 8.41689 7.66725 8.40127 7.8252 8.43262ZM11.333 8.41699C11.549 8.41699 11.7564 8.50258 11.9092 8.65527C12.062 8.80807 12.1484 9.01536 12.1484 9.23145C12.1484 9.3926 12.1003 9.55057 12.0107 9.68457C11.9213 9.81833 11.7942 9.92274 11.6455 9.98438C11.4967 10.046 11.3328 10.0616 11.1748 10.0303C11.0167 9.99883 10.8708 9.92157 10.7568 9.80762C10.643 9.69373 10.5656 9.54853 10.5342 9.39062C10.5028 9.2327 10.5185 9.06871 10.5801 8.91992C10.6417 8.77103 10.7469 8.64324 10.8809 8.55371C11.0147 8.46436 11.1721 8.41706 11.333 8.41699ZM5 4.75H2C1.44772 4.75 1 5.19772 1 5.75V6.75H0V5.75C0 4.64543 0.895431 3.75 2 3.75H5V4.75ZM17.2041 3.76074C18.2128 3.863 19 4.71435 19 5.75V6.75H18V5.75C18 5.19772 17.5523 4.75 17 4.75H14V3.75H17L17.2041 3.76074Z" />
    </svg>
  );
};
